import { ClientRole } from '@cognitoforms/types/server-types/core/services/client-role';
import { UserPinType } from '@cognitoforms/types/server-types/model/user-pin-type';
import type { ITreeNode } from 'src/components/navigation/FormTree.vue';
import { TreeNodeType } from 'src/components/navigation/FormTree.vue';
import { globalStore } from 'src/stores/global-store';
import { computed, type Ref } from 'vue';

export type TreeNodes = {
	defaultFormTree: Ref<ITreeNode[]>;
	archivedFormTree: Ref<ITreeNode[]>;
	tasksFormTree: Ref<ITreeNode[]>;
	userPinsTree: Ref<ITreeNode[]>;
}

export const useTreeNodes = (): TreeNodes => {
	const sortNodes = (nodes: ITreeNode[]) => {
		// Sort node children
		nodes.forEach(node => {
			node.children = sortNodes(node.children);
		});

		return nodes.sort((a, b) => {
			// Sort folders before forms
			if (a.type === TreeNodeType.Folder && b.type !== TreeNodeType.Folder)
				return -1;
			if (a.type !== TreeNodeType.Folder && b.type === TreeNodeType.Folder)
				return 1;

			// Sort folders and forms by alphabetically
			if (a.type !== TreeNodeType.View && b.type !== TreeNodeType.View)
				return a.name.localeCompare(b.name);

			return 0;
		});
	};

	const sortPins = (pins: ITreeNode[]) =>{
		const pinOrder = globalStore.userPins.map(p=>p.Id);
		return pins.filter(p => p.formMeta).sort((a, b) => {	// filter for only form nodes first, then sort
			const indexA = pinOrder.indexOf(a.id);
			const indexB = pinOrder.indexOf(b.id);
			return indexA - indexB;
		});
	};

	const removeFoldersWithoutForms = (nodes: ITreeNode[], keepAdminFolders = false) => {
		return nodes.filter(node => {
			const isFolder = node.type === TreeNodeType.Folder || node.type === TreeNodeType.Archive;
			if (isFolder)
				node.children = removeFoldersWithoutForms(node.children, keepAdminFolders);

			const canManageFolder = keepAdminFolders && (node.type === TreeNodeType.Folder) ? node.folderMeta.CanManageFolder : false;

			return !isFolder || node.children.length > 0 || canManageFolder || (node.type === TreeNodeType.Archive && globalStore.hasArchivedForms);
		});
	};

	const treeNodes = computed(() => {
		const defaultFormTree: ITreeNode[] = [];
		const archivedFormTree: ITreeNode[] = [];
		const tasksFormTree: ITreeNode[] = [];
		const userPinsTree: ITreeNode[] = [];

		const isGuest = globalStore.organization.Role === ClientRole.Guest;

		// Add archive node to archived tree as the root
		let archivedRoot = archivedFormTree;
		archivedFormTree.push({ id: 'archive', name: 'Archived', type: TreeNodeType.Archive, isPinned: false, children: [], canPin: false });
		archivedRoot = archivedFormTree[0].children;

		const userPins = globalStore.userPins.reduce((dictionaryItem, pin)=> {
			dictionaryItem[pin.Id] = pin;
			return dictionaryItem;
		}, {});

		const canHaveFolders = globalStore.organization.Plan?.AllowCreateFolders;

		if (canHaveFolders)
			Object.values(globalStore.folders).forEach(folderMeta => {
				const folderNode: ITreeNode = {
					folderMeta,
					type: TreeNodeType.Folder,
					id: folderMeta.Id,
					name: folderMeta.Name,
					isPinned: false,
					canPin: false,
					children: []
				};

				if (!folderMeta.IsArchived) {
					const defaultFolderNode = structuredClone(folderNode);
					defaultFormTree.push(defaultFolderNode);
				}

				// Always push folder to archive tree since any forms in folder could be archived
				archivedRoot.push(folderNode);
			});

		Object.values(globalStore.forms).forEach(formMeta => {
			const isPinned = formMeta.Id in userPins;
			const formNode: ITreeNode = {
				formMeta,
				type: TreeNodeType.Form,
				id: formMeta.Id,
				name: formMeta.Name,
				isPinned: isPinned,
				canPin: !isGuest && !isPinned && !formMeta.IsArchived,
				children: []
			};

			// Append form to correct folder or root if uncategorized; append to archive tree if form is archived
			if (!formMeta.IsArchived) {
				const defaultTreeParent = formMeta.FolderId ? defaultFormTree.find(node => node.id === formMeta.FolderId)?.children ?? defaultFormTree : defaultFormTree;

				defaultTreeParent.push(formNode);
				if (formMeta.Id in userPins) {
					const pinnedFormNode : ITreeNode = {
						...formNode,
						children: []
					};
					userPinsTree.push(pinnedFormNode);
				}
			}
			else {
				const archiveTreeParent = formMeta.FolderId ? archivedRoot.find(node => node.id === formMeta.FolderId)?.children ?? archivedRoot : archivedRoot;
				archiveTreeParent.push(formNode);
			}

			const canHaveSavedEntryViews = globalStore.organization.Plan?.AllowEntryViews;

			if (!formMeta.IsSingleViewForm) {
				// Create view nodes and attach to their form
				formMeta.EntryViews.forEach(viewMeta => {
					const viewNode: ITreeNode = {
						viewMeta,
						type: TreeNodeType.View,
						id: viewMeta.Id,
						name: viewMeta.Name,
						isPinned: viewMeta.Id in userPins,
						canPin: false,
						children: []
					};

					// locked saved views if not available on plan
					if (!canHaveSavedEntryViews)
						viewNode.isLocked = !viewNode.id.endsWith('-0');

					formNode.children.push(viewNode);

					// Add to pin tree if in userPins list
					if (viewMeta.Id in userPins && !formMeta.IsArchived) {
						const pinViewNode = {
							...viewNode,
							id: globalStore.getEffectivePin(viewNode.id)
						};
						userPinsTree.push(pinViewNode);
					}
				});
			}
			else {
				// Looping through stored pins to see if any correspond to a stored EntryView on the form
				const pinnedViews = globalStore.userPins.filter(view => view.Id === formMeta.Id && view.Type === UserPinType.EntryView);
				if (pinnedViews.length) {
					pinnedViews.forEach(storedPin => {
						const effectivePinId = globalStore.getEffectivePin(storedPin.Id);
						const viewMeta = formMeta.EntryViews.find(view => view.Id === effectivePinId);
						if (!viewMeta) {
							const viewNode: ITreeNode = {
								viewMeta,
								type: TreeNodeType.View,
								id: effectivePinId,
								name: formMeta.Name, // Use form name for single view pins
								isPinned: viewMeta.Id in userPins,
								canPin: false,
								children: []
							};

							userPinsTree.push(viewNode);
						}
					});
				}
			}

			// Handle task tree - only append views with tasks
			const taskViewsOnly = formNode.children.filter(v => v.viewMeta.IsTaskView && v.viewMeta.Entries > 0);
			if (!formMeta.IsArchived && (taskViewsOnly.length > 0 || (formMeta.IsSingleViewForm && formMeta.Tasks > 0))) {
				const formNodeWithTasks = structuredClone(formNode);

				if (!formMeta.IsSingleViewForm)
					formNodeWithTasks.children = taskViewsOnly;
				else
					formNodeWithTasks.children = [];

				// Expand forms by default
				tasksFormTree.push(formNodeWithTasks);
			}
		});

		return {
			defaultFormTree,
			archivedFormTree,
			tasksFormTree,
			userPinsTree
		};
	});

	return {
		defaultFormTree: computed(() => removeFoldersWithoutForms(sortNodes(treeNodes.value.defaultFormTree), true)),
		archivedFormTree: computed(() => removeFoldersWithoutForms(sortNodes(treeNodes.value.archivedFormTree))),
		tasksFormTree: computed(() => sortNodes(treeNodes.value.tasksFormTree)),
		userPinsTree: computed(() => sortPins(treeNodes.value.userPinsTree))
	};
};